/* eslint-disable @typescript-eslint/no-explicit-any */

import { scaleLinear, scaleOrdinal as createOrdinalScale } from '@visx/scale';
import { useContext, useMemo } from 'react';
import ThemeContext from "../context/ThemeContext.js";
import DataContext from "../context/DataContext.js";
import useDataRegistry from "../hooks/useDataRegistry.js";
import useDimensions from "../hooks/useDimensions.js";
import useScales from "../hooks/useScales.js";
import isDiscreteScale from "../utils/isDiscreteScale.js";
/** Props that can be passed to initialize/update the provider config. */
import { jsx as _jsx } from "react/jsx-runtime";
export default function DataProvider(_ref) {
  let {
    initialDimensions,
    theme: propsTheme,
    xScale: xScaleConfig,
    yScale: yScaleConfig,
    children,
    horizontal: initialHorizontal = 'auto',
    resizeObserverPolyfill
  } = _ref;
  // `DataProvider` provides a theme so that `ThemeProvider` is not strictly needed.
  // `props.theme` takes precedent over `context.theme`, which has a default even if
  // a ThemeProvider is not present.
  const contextTheme = useContext(ThemeContext);
  const theme = propsTheme || contextTheme;
  const [{
    width,
    height,
    margin
  }, setDimensions] = useDimensions(initialDimensions);
  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);
  const dataRegistry = useDataRegistry();
  const {
    xScale,
    yScale
  } = useScales({
    dataRegistry,
    xScaleConfig,
    yScaleConfig,
    xRange: [margin.left, Math.max(0, width - margin.right)],
    yRange: [Math.max(0, height - margin.bottom), margin.top]
  });
  const registryKeys = dataRegistry.keys();
  const colorScale = useMemo(() => createOrdinalScale({
    domain: registryKeys,
    range: theme.colors
  }), [registryKeys, theme.colors]);
  const horizontal = initialHorizontal === 'auto' ? isDiscreteScale(yScaleConfig) || yScaleConfig.type === 'time' || yScaleConfig.type === 'utc' : initialHorizontal;
  const value = useMemo(() => ({
    dataRegistry,
    registerData: dataRegistry.registerData,
    unregisterData: dataRegistry.unregisterData,
    xScale: xScale || scaleLinear(),
    yScale: yScale || scaleLinear(),
    colorScale,
    theme,
    width,
    height,
    margin,
    innerWidth,
    innerHeight,
    setDimensions,
    horizontal,
    resizeObserverPolyfill
  }),
  // everything here should be memoized between renders
  // to avoid child re-renders
  [colorScale, dataRegistry, height, horizontal, innerHeight, innerWidth, margin, setDimensions, theme, width, xScale, yScale, resizeObserverPolyfill]);
  return /*#__PURE__*/_jsx(DataContext.Provider, {
    value: value,
    children: children
  });
}